import { isNil } from 'lodash';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import {
  useUpdateOrderMutation,
  useUpsertAddressMutation,
} from '../../apis/mlkApi/mlk.api.hooks';
import { parseRes } from '../../apis/utils';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import { LatLng } from '../../components/Map/Map';
import { ModalRef } from '../../components/Modal/Modal';
import { Stepper } from '../../components/Stepper/Stepper';
import { getIsEmailConfirmed } from '../../helpers/auth';
import { useBack } from '../../hooks/useBack';
import { AddressParts } from '../../hooks/useDesktopGoogleApi';
import { AddressNoteForm } from '../../hooks/validations/useAddressNoteValidationForm';
import { useLanguage } from '../../locale/useLanguage';
import { Address } from '../../models/address.models';
import { useAppDispatch, useAppSelector } from '../../redux/core';
import { setOperation } from '../../redux/general/general.actions';
import { getOperationSelector } from '../../redux/general/general.selectors';
import { authTokenSelector } from '../../redux/user/user.selectors';
import { Routes } from '../../routes/types';
import { AddressNotes } from './components/AddressNotes/AddressNotes';
import { SaveAddressConfirmModal } from './components/SaveAddressConfirmModal/SaveAddressConfirmModal';
import { DeliveryPoint } from './components/DeliveryPoint/DeliveryPoint';
import { EditAddressSuccess } from './components/EditAddressSuccess/EditAddressSuccess';
import { InsertAddress } from './components/InsertAddress/InsertAddress';
import { SaveModal } from './components/SaveModal/SaveModal';
import { VerifyMapPosition } from './components/VerifyMapPosition/VerifyMapPosition';
import {
  buildAddressFromForms,
  buildReceptionDays,
  transformAddressInEditAddressModels,
} from './createOrEditAddressHelper';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { Order } from '../../models';

import './CreateOrEditAddressView.css';
import { GaEvent, pushEvent } from '../../helpers/googleAnalytics';
import { getCorrectStatus } from '../../helpers/order';

type CreateOrEditAddressLocationState =
  | {
      isFromChangeAddress?: boolean;
      isFromConvergence?: boolean;
    }
  | undefined;

type CreateOrEditAddressViewProps = {
  address?: Address;
  title?: string;
};

export const CreateOrEditAddressView: React.FC<
  CreateOrEditAddressViewProps
> = ({ address, title }) => {
  // General variables
  const [t] = useLanguage();
  const location = useLocation<CreateOrEditAddressLocationState>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const saveModalRef = useRef<ModalRef>(null);
  const saveAddressModalRef = useRef<ModalRef>(null);
  const historyBack = useBack();

  const accessToken = useAppSelector(authTokenSelector);

  // Variables relative to the light sign in flow
  const isUserLoggedIn: boolean = !!useAppSelector(authTokenSelector);
  /*
    operation.savedData: {
      step?: number
      notes?: AddressNotes,
      address?: AddressParts
      customCoordinates?: LatLng
    }
    */
  const operation = useSelector(getOperationSelector);

  const transformedAddress = useMemo(() => {
    if (isNil(address)) return undefined;
    return transformAddressInEditAddressModels(address);
  }, [address]);

  const [step, setStep] = useState(operation?.savedData.step || 0);
  const [orderId, setOrderId] = useState<number>();
  const [order, setOrder] = useState<Order>();
  const [territoryId, setTerritoryId] = useState<string>();

  // Variable relative the new address flow
  const [addressParts, setAddressParts] = useState<AddressParts>(
    transformedAddress?.addressParts || operation?.savedData.address
  );
  const [customCoordinates, setCustomCoordinates] = useState<LatLng>(
    transformedAddress?.customCoordinates ||
      (operation?.savedData.customCoordinates as LatLng)
  );
  const [isFinishVisible, setIsFinishVisible] = useState<boolean>();
  const [addressNotes, setAddressNotes] = useState<AddressNoteForm>(
    transformedAddress?.addressNotes ||
      (operation?.savedData.notes as AddressNoteForm)
  );
  const [addressId, setAddressId] = useState<number | undefined>(address?.id);
  const [upsertAddress] = useUpsertAddressMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const hasNotes = Boolean(addressNotes);

  const isEmailConfirmed = useMemo(() => {
    if (accessToken) {
      return getIsEmailConfirmed(accessToken);
    }

    return null;
  }, [accessToken]);

  const goBack = useCallback(() => {
    dispatch(setOperation(null));
    historyBack();
  }, [dispatch, historyBack]);

  const updateOperation = useCallback(
    (data: any) => {
      if (operation) {
        dispatch(
          setOperation({
            ...operation,
            savedData: {
              ...operation.savedData,
              ...data,
            },
          })
        );
      }
    },
    [dispatch, operation]
  );

  const setEventOrder = useCallback(() => {
    if (order) {
      const calculatedOrderStatus = getCorrectStatus(order);
      pushEvent(GaEvent.ChangeAddress, {
        name: GaEvent.ChangeAddress,
        shipper_order_code: order.shipperOrderCode,
        milkman_order_state: order.status,
        order_id: order.id,
        shipper_id: order.shipper.id,
        shipper_name: order.shipper.name,
        order_state_calculated: calculatedOrderStatus!,
        territory_id: +order.shipperTerritoryConfiguration.territoryId,
      });
    }
  }, [order]);

  const navigateBack = useCallback(() => {
    if (step > 0) {
      const newStep = step - 1;
      updateOperation({ step: newStep });
      setStep(newStep);
    } else {
      goBack();
    }
  }, [goBack, step, updateOperation]);

  const changeDeliveryAddress = useCallback(
    async (
      address: AddressParts,
      coordinates?: LatLng,
      notes?: AddressNoteForm,
      onApiCall?: (
        res:
          | {
              data: Order;
            }
          | {
              error: FetchBaseQueryError | SerializedError;
            }
      ) => void
    ) => {
      saveAddressModalRef.current?.closeModal();
      const receptionDays = notes ? buildReceptionDays(notes) : undefined;
      try {
        const res = await updateOrder({
          orderId: `${orderId}`,
          address: {
            address: address.formattedAddress,
            city: address.aal3,
            lat: address.lat,
            lon: address.lng,
            country: address.country,
            route: address.street,
            number: address.streetNumber,
            zipCode: address.zipCode,
            doorbellLatitude: coordinates?.lat || addressParts.lat,
            doorbellLongitude: coordinates?.lng || addressParts.lng,
          },
          reception: receptionDays,
          instructions: notes
            ? {
                instructions: notes.notes,
                doorbell: notes.intercomName,
                elevator: notes.hasElevator,
                floorNumber: notes.floor ? +notes.floor : undefined,
              }
            : undefined,
        }).unwrap();
        onApiCall?.({ data: res });
      } catch (e) {
        // TODO: Handle error
      }
    },
    [addressNotes, addressParts, orderId, updateOrder]
  );

  const saveAddressToFavorites = useCallback(
    async (
      addressName?: string,
      address?: AddressParts,
      coordinates?: LatLng,
      notes?: AddressNoteForm
    ) => {
      const receptionDays = notes ? buildReceptionDays(notes) : undefined;
      const addressBody = buildAddressFromForms(
        addressId,
        address || addressParts,
        coordinates || customCoordinates,
        notes || addressNotes,
        receptionDays,
        addressName
      );
      saveAddressModalRef.current?.closeModal();
      try {
        const res = await upsertAddress(addressBody).unwrap();
        if (location.state?.isFromChangeAddress) {
          setEventOrder();
          setIsFinishVisible(true);
        } else {
          history.replace(Routes.Addresses);
        }
        setAddressId(res.id);
      } catch (e) {
        // TODO: Do something in case of error
      }
    },
    [
      addressId,
      addressNotes,
      addressParts,
      customCoordinates,
      history,
      location.state?.isFromChangeAddress,
      setEventOrder,
      upsertAddress,
    ]
  );

  const saveAddress = useCallback(
    (address?: AddressParts, coordinates?: LatLng, notes?: AddressNoteForm) => {
      if (location.state?.isFromChangeAddress) {
        changeDeliveryAddress(address!, coordinates, notes, (res) => {
          const { errorRes, successRes } = parseRes(res);
          if (successRes.data) {
            dispatch(setOperation(null));
            if (isUserLoggedIn && isEmailConfirmed) {
              if (!notes?.addressName) {
                saveAddressModalRef.current?.openModal();
              } else {
                saveAddressToFavorites(undefined, address, coordinates, notes);
              }
            } else {
              setEventOrder();
              setIsFinishVisible(true);
            }
          }
          if (errorRes.error) {
            // TODO: Handle error,
            console.log(errorRes.error);
          }
        });
      } else {
        if (!notes?.addressName) {
          saveAddressModalRef.current?.openModal();
        } else {
          saveAddressToFavorites(undefined, address, coordinates, notes);
        }
      }
    },
    [
      changeDeliveryAddress,
      dispatch,
      isEmailConfirmed,
      isUserLoggedIn,
      location.state?.isFromChangeAddress,
      saveAddressToFavorites,
      setEventOrder,
    ]
  );

  const finishWizard = useCallback(
    (notes: AddressNoteForm) => {
      if (!isUserLoggedIn || !isEmailConfirmed) {
        updateOperation({ notes });
      }
      setAddressNotes(notes);
      saveAddress(addressParts, customCoordinates, notes);
    },
    [
      addressParts,
      customCoordinates,
      isEmailConfirmed,
      isUserLoggedIn,
      saveAddress,
      updateOperation,
    ]
  );

  const nextStep = useCallback(() => {
    const newStep = step + 1;
    if (!isUserLoggedIn || !isEmailConfirmed) {
      updateOperation({ step: newStep });
    }
    setStep(newStep);
  }, [isEmailConfirmed, isUserLoggedIn, step, updateOperation]);

  const onAddressInserted = useCallback(
    (insertedAddress: AddressParts) => {
      nextStep();
      if (!isUserLoggedIn || !isEmailConfirmed) {
        updateOperation({ address: insertedAddress });
      }
      setAddressParts(insertedAddress);
    },
    [isEmailConfirmed, isUserLoggedIn, nextStep, updateOperation]
  );

  const onSaveAndClose = useCallback(
    (latLng: LatLng) => {
      if (!isUserLoggedIn || !isEmailConfirmed) {
        updateOperation({ customCoordinates: latLng });
      }
      saveAddress(addressParts, latLng, addressNotes);
    },
    [
      isUserLoggedIn,
      isEmailConfirmed,
      saveAddress,
      addressParts,
      addressNotes,
      updateOperation,
    ]
  );

  const onAddNotes = useCallback(
    (latLng: LatLng) => {
      if (!isUserLoggedIn || !isEmailConfirmed) {
        updateOperation({ customCoordinates: latLng });
      }
      setCustomCoordinates(latLng);
      nextStep();
    },
    [isEmailConfirmed, isUserLoggedIn, nextStep, updateOperation]
  );

  const editAddressNotes = useCallback(() => {
    setIsFinishVisible(false);
  }, []);

  const goToHome = useCallback(() => {
    history.push(Routes.Home);
  }, [history]);

  const skipNotes = useCallback(() => {
    saveAddress(addressParts, customCoordinates, addressNotes);
  }, [addressParts, addressNotes, customCoordinates, saveAddress]);

  const onExit = useCallback(() => {
    if (step === 0) {
      history.push(Routes.Home);
    } else {
      saveModalRef.current?.openModal();
    }
  }, [history, step]);

  const closeChangeAddressConfirmModal = useCallback(() => {
    saveAddressModalRef.current?.closeModal();
    if (location.state?.isFromChangeAddress) {
      setIsFinishVisible(true);
    }
  }, [location.state?.isFromChangeAddress]);

  const continueWithAddressSubmission = useCallback(() => {
    saveModalRef.current?.closeModal();
  }, []);

  const exitFromAddressSubmission = useCallback(() => {
    saveModalRef.current?.closeModal();
    dispatch(setOperation(null));
    history.push(Routes.Home);
  }, [dispatch, history]);

  const stepToShow = useMemo(() => {
    switch (step) {
      case 0:
        return (
          <InsertAddress
            territoryId={territoryId}
            insertedAddress={addressParts}
            onContinue={onAddressInserted}
          />
        );
      case 1:
        return (
          <VerifyMapPosition onContinue={nextStep} address={addressParts!} />
        );
      case 2:
        return (
          <DeliveryPoint
            onAddNotes={onAddNotes}
            onSaveAndClose={onSaveAndClose}
            doorbellLat={customCoordinates?.lat}
            doorbellLng={customCoordinates?.lng}
            address={addressParts!}
          />
        );
      case 3:
        return (
          <AddressNotes
            onSkip={skipNotes}
            onSaveNotes={finishWizard}
            notes={addressNotes}
            isFromChangeAddress={location.state?.isFromChangeAddress}
            isUserLogged={isUserLoggedIn}
          />
        );
      default:
        return <div>Something gone wrong in steps</div>;
    }
  }, [
    step,
    territoryId,
    addressParts,
    onAddressInserted,
    nextStep,
    onAddNotes,
    onSaveAndClose,
    customCoordinates?.lat,
    customCoordinates?.lng,
    skipNotes,
    finishWizard,
    addressNotes,
    location.state?.isFromChangeAddress,
    isUserLoggedIn,
  ]);

  const setupOrded = useCallback(
    ({ data: order }: OrderRequestResult) => {
      if (order) {
        setOrderId(order.id);
        setOrder(order);
      }
      if (order && location.state?.isFromChangeAddress) {
        setTerritoryId(order.shipperTerritoryConfiguration.territoryId);
      }
      if (order && location.state?.isFromConvergence) {
        setAddressParts({
          aal1: order?.deliveryAddress.country,
          aal2: order?.deliveryAddress.country,
          aal3: order?.deliveryAddress.city,
          country: order?.deliveryAddress.country,
          formattedAddress: order?.deliveryAddress.address,
          lat: order?.deliveryAddress.lat,
          lng: order?.deliveryAddress.lon,
          street: order?.deliveryAddress.route,
          zipCode: order?.deliveryAddress.zipCode,
          streetNumber: order?.deliveryAddress.number,
        });
        setStep(2);
      }
    },
    [location.state?.isFromChangeAddress, location.state?.isFromConvergence]
  );

  return isFinishVisible ? (
    <EditAddressSuccess
      hasNotes={hasNotes}
      onBackToEdit={editAddressNotes}
      onTrackOrder={goToHome}
    />
  ) : (
    <Layout
      title={title || t('pageTitles.newAddress')}
      hasBack
      onBackClick={navigateBack}
      hasClose
      onCloseClick={onExit}
      onOrderChange={setupOrded}
      orderIdOptional
    >
      <SaveModal
        ref={saveModalRef}
        onExit={exitFromAddressSubmission}
        onSave={continueWithAddressSubmission}
      />
      <SaveAddressConfirmModal
        onSave={saveAddressToFavorites}
        onExit={closeChangeAddressConfirmModal}
        ref={saveAddressModalRef}
      />
      <div className="new-address--body">
        <div className="new-address--stepper">
          <Stepper step={step} stepsCount={4} />
        </div>
        <div className="new-address--step-container">{stepToShow}</div>
      </div>
    </Layout>
  );
};
